import { resolveApiMessage, showToast } from '../../../helpers';
import {
  CONTENT_CUSTOMIZATION,
  DELIVERY_METHODS,
  getOpsProgramMeetingIcs,
  LEARNER_REGISTRATION_METHODS,
  MEETING_STATUSES,
  PRICING_OPTIONS,
  TOpsLedgerRow,
  TOpsMeetingKind,
  TOpsProgramFields,
  TOpsProgramFieldsUpdate,
} from '../../../requests/opsRegistrationsExtra';

export const STATUSES = [
  'Utilization',
  'Onboarding',
  'Implementation',
  'Pre Launch',
  'Renewal',
  'Draft',
  'Completed',
  'Expired',
];

export const money = (v: number | null | undefined) =>
  v == null
    ? '—'
    : v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// Signed money for ledger rows: a reversal or credit reads as −$212.50
export const signed = (v: number) => (v < 0 ? `−${money(-v)}` : money(v));

export const LEDGER_LABEL: Record<string, string> = {
  deduction: 'Deduction',
  reversal: 'Reversal',
  adjustment: 'Adjustment',
  'top-up': 'Funds added',
  skipped: 'Not charged',
  'deduction-dryrun': 'Rehearsal',
  'adjustment-dryrun': 'Rehearsal',
  'top-up-dryrun': 'Rehearsal',
};

export const ledgerTone = (row: TOpsLedgerRow) => {
  if (!row.live) return 'neutral' as const;
  if (row.type === 'reversal') return 'warn' as const;
  if (row.type === 'skipped') return 'info' as const;
  return 'good' as const;
};

// ── The field panels ───────────────────────────────────────────────────
// Every field here is a Salesforce Contract field the mirror already
// carries (src/ops/programs/program-fields.ts); the portal writes it and
// the nightly sync leaves it alone. Sections and order follow the
// Salesforce layout the team fills today.
export type FieldKind =
  | 'text'
  | 'textarea'
  | 'url'
  | 'number'
  | 'bool'
  | 'select'
  | 'multi'
  | 'contact'
  | 'readonly';
export type FieldSpec = {
  key: keyof TOpsProgramFields;
  label: string;
  kind: FieldKind;
  options?: readonly string[];
  hint?: string;
  wide?: boolean;
  suffix?: string;
};
export type PanelId = 'info' | 'strategy' | 'portal';

export const PANELS: Record<
  PanelId,
  { title: string; note?: string; fields: FieldSpec[] }
> = {
  info: {
    title: 'Program information',
    fields: [
      {
        key: 'deliveryMethods',
        label: 'Delivery methods used',
        kind: 'multi',
        options: DELIVERY_METHODS,
      },
      {
        key: 'contentCustomization',
        label: 'Content customization',
        kind: 'select',
        options: CONTENT_CUSTOMIZATION,
      },
      { key: 'opportunityType', label: 'Opportunity type', kind: 'readonly' },
      {
        key: 'opportunitySubType',
        label: 'Opportunity sub-type',
        kind: 'readonly',
      },
      {
        key: 'publicDiscount',
        label: 'Public discount',
        kind: 'number',
        suffix: '%',
      },
      {
        key: 'privateDiscount',
        label: 'Private discount',
        kind: 'number',
        suffix: '%',
      },
      {
        key: 'pricing',
        label: 'Pricing',
        kind: 'select',
        options: PRICING_OPTIONS,
      },
      {
        key: 'customPricingNotes',
        label: 'Custom pricing notes',
        kind: 'textarea',
      },
      {
        key: 'allowPublicEnrollment',
        label: 'Allow public enrolment',
        kind: 'bool',
        hint: 'Members may register on public classes against the program',
      },
      {
        key: 'adminPortalAccess',
        label: 'Admin portal access',
        kind: 'bool',
      },
      { key: 'maxMembers', label: 'Max program members', kind: 'number' },
      {
        key: 'authorizedMembers',
        label: 'Authorized members',
        kind: 'number',
      },
      {
        key: 'contractPocId',
        label: 'Contract POC',
        kind: 'contact',
        hint: 'A contact on this account',
      },
      {
        key: 'secondaryContactId',
        label: 'Secondary contact',
        kind: 'contact',
        hint: 'Aim for two named people',
      },
      {
        key: 'descriptionOfSale',
        label: 'Description of what was sold',
        kind: 'textarea',
        wide: true,
      },
      {
        key: 'clientFolderUrl',
        label: 'Custom outlines – client folder',
        kind: 'url',
        wide: true,
        hint: 'SharePoint folder link',
      },
      {
        key: 'programLink',
        label: 'Program link',
        kind: 'url',
        wide: true,
        hint: 'Generated in Salesforce for its programs; set it here for one born in the portal',
      },
      {
        key: 'kickOffMeeting',
        label: 'Primary POC kick-off meeting',
        kind: 'select',
        options: MEETING_STATUSES,
      },
      {
        key: 'prepCallRequirements',
        label: 'Prep call requirements',
        kind: 'text',
        hint: 'What every prep call on this program must cover',
      },
      {
        key: 'prepCallRequirementsNotes',
        label: 'Prep call requirements notes',
        kind: 'textarea',
        wide: true,
      },
      {
        key: 'learnerOrientation',
        label: 'Learner orientation',
        kind: 'text',
        hint: 'Scheduled / done / not needed, and when',
      },
      {
        key: 'wrapDebrief',
        label: 'Program wrap / debrief',
        kind: 'select',
        options: MEETING_STATUSES,
      },
    ],
  },
  strategy: {
    title: 'Program level strategy',
    note: 'Internal — not shown to trainers or learners.',
    fields: [
      {
        key: 'clientProgramGoals',
        label: 'Client program goals',
        kind: 'textarea',
        wide: true,
      },
      {
        key: 'successCriteria',
        label: 'Success criteria',
        kind: 'textarea',
        wide: true,
      },
      {
        key: 'priorityLearningAreas',
        label: 'Client priority learning areas',
        kind: 'textarea',
        wide: true,
      },
      {
        key: 'relatedInitiatives',
        label: 'Related organizational initiatives',
        kind: 'textarea',
        wide: true,
      },
      {
        key: 'clientMeetingNotes',
        label: 'Client meeting notes',
        kind: 'textarea',
        wide: true,
        hint: 'The discovery summary and each review — sales pastes, CS keeps it going',
      },
    ],
  },
  portal: {
    title: 'Portal message',
    note: 'What learners on this program see when they sign in. The learner portal reads this from Salesforce today and will read the copy kept here once the learner-side migration lands.',
    fields: [
      {
        key: 'portalMessageHeadline',
        label: 'Headline',
        kind: 'text',
        wide: true,
      },
      { key: 'portalMessageBody', label: 'Body', kind: 'textarea', wide: true },
      {
        key: 'learnerRegistrationMethod',
        label: 'Learner registration method',
        kind: 'select',
        options: LEARNER_REGISTRATION_METHODS,
      },
    ],
  },
};

// The ten Program Information fields of the handoff checklist (T2.02), for
// the gaps indicator. The two checkboxes always hold a value.
export const GAP_LABELS: Record<string, string> = {
  deliveryMethods: 'Delivery methods',
  contentCustomization: 'Content customization',
  clientFolderUrl: 'Client folder',
  adminPortalAccess: 'Admin portal access',
  allowPublicEnrollment: 'Allow public enrolment',
  descriptionOfSale: 'Description of what was sold',
  pricing: 'Pricing',
  customPricingNotes: 'Custom pricing notes',
  publicDiscount: 'Public discount',
  privateDiscount: 'Private discount',
};

export type Draft = Record<string, string | boolean>;

/** Draft values (strings for inputs) from the saved fields, for one panel */
export function draftFor(panel: PanelId, f: TOpsProgramFields): Draft {
  const d: Draft = {};
  for (const spec of PANELS[panel].fields) {
    const v = f[spec.key];
    if (spec.kind === 'bool') d[spec.key] = Boolean(v);
    else if (spec.kind === 'readonly') continue;
    else d[spec.key] = v == null ? '' : String(v);
  }
  return d;
}

/** The PATCH body for one panel: every editable field, so clears are sent */
export function bodyFor(panel: PanelId, d: Draft): TOpsProgramFieldsUpdate {
  const out: Record<string, unknown> = {};
  for (const spec of PANELS[panel].fields) {
    if (spec.kind === 'readonly') continue;
    const v = d[spec.key];
    if (spec.kind === 'bool') out[spec.key] = Boolean(v);
    else if (spec.kind === 'number')
      out[spec.key] = v === '' || v == null ? null : Number(v);
    else out[spec.key] = typeof v === 'string' ? v.trim() : '';
  }
  return out as TOpsProgramFieldsUpdate;
}

/** Several long-text fields arrive as HTML from Salesforce's rich text. */
export function stripHtml(v: string): string {
  return v
    .replace(/<\/(p|div|li|br)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&#39;|&rsquo;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Download a scheduled kickoff or wrap as an .ics file. */
export async function downloadMeetingIcs(
  programId: string,
  kind: TOpsMeetingKind,
  programName: string | null | undefined,
) {
  try {
    const blob = await getOpsProgramMeetingIcs(programId, kind);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${kind}-${programName ?? 'program'}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    showToast(resolveApiMessage(err), 'error');
  }
}
